import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { 
  TrendingUp, CloudSun, DollarSign, Zap, Bell, 
  ChevronDown, ChevronUp, Sparkles
} from "lucide-react";
import { API } from "../lib/api";
import { useLang } from "../context/LangContext";
import { useMember } from "../context/MemberContext";

const MobileHeader = () => {
  const [breakingNews, setBreakingNews] = useState([]);
  const [rates, setRates] = useState(null);
  const [weather, setWeather] = useState(null);
  const [showMarket, setShowMarket] = useState(false);
  const [currentNewsIndex, setCurrentNewsIndex] = useState(0);
  const { t } = useLang();
  const { member } = useMember();
  const tickerRef = useRef(null);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [newsRes, marketRes] = await Promise.all([
          axios.get(`${API}/news?limit=5`),
          axios.get(`${API}/market`)
        ]);
        setBreakingNews(newsRes.data.slice(0, 5));
        setRates(marketRes.data);
      } catch (e) { console.error(e); }

      // Weather
      try {
        const wRes = await axios.get('https://wttr.in/Istanbul?format=j1');
        const current = wRes.data.current_condition?.[0];
        if (current) {
          setWeather({ 
            temp: current.temp_C, 
            desc: current.lang_tr?.[0]?.value || current.weatherDesc?.[0]?.value || '' 
          });
        }
      } catch (e) { console.error(e); }
    };
    fetchAll();
  }, []);

  // Auto-rotate breaking news
  useEffect(() => {
    if (breakingNews.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentNewsIndex(prev => (prev + 1) % breakingNews.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [breakingNews.length]);

  return (
    <header className="lg:hidden sticky top-0 z-40" data-testid="mobile-header">
      {/* Main Header Bar */}
      <div className="bg-[#0a0a0b]/95 backdrop-blur-xl border-b border-zinc-800/50">
        <div className="flex items-center justify-between h-14 px-4">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="mobile-logo">
            <div className="w-9 h-9 bg-gradient-to-r from-orange-500 to-amber-400 rounded-xl flex items-center justify-center shadow-lg shadow-orange-500/25">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="18" rx="1.5" />
                <rect x="8" y="3" width="13" height="18" rx="1.5" />
                <line x1="11" y1="7" x2="18" y2="7" />
                <line x1="11" y1="10.5" x2="17" y2="10.5" />
                <line x1="11" y1="14" x2="16" y2="14" />
              </svg>
            </div>
            <div className="flex flex-col">
              <span className="text-lg font-bold text-white leading-none">
                loft<span className="text-amber-500">.press</span>
              </span>
            </div>
          </Link>

          {/* Right side: Weather + Market + AI Button */}
          <div className="flex items-center gap-2">
            {weather && (
              <div className="flex items-center gap-1 text-xs text-zinc-400">
                <CloudSun className="w-4 h-4 text-amber-500" />
                <span className="text-white font-medium">{weather.temp}°</span>
              </div>
            )}
            
            {/* Market Toggle - Next to temperature */}
            <button 
              onClick={() => setShowMarket(!showMarket)}
              className="flex items-center gap-1 px-2 py-1 bg-zinc-800/80 border border-zinc-700/50 rounded-full text-[10px] text-zinc-400"
              data-testid="mobile-market-toggle"
            >
              <TrendingUp className="w-3 h-3 text-emerald-400" />
              {showMarket ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
            
            <Link 
              to="/ai-asistan" 
              className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-blue-500/20 to-purple-500/20 border border-blue-500/30 rounded-full"
              data-testid="mobile-ai-btn"
            >
              <Sparkles className="w-3.5 h-3.5 text-blue-400" />
              <span className="text-xs font-medium text-blue-400">AI</span>
            </Link>

            {member && (
              <Link to="/profil" className="relative p-1.5" data-testid="mobile-notif-btn">
                <Bell className="w-5 h-5 text-zinc-400" />
                <span className="absolute top-0.5 right-0.5 w-2 h-2 bg-red-500 rounded-full" />
              </Link>
            )}
          </div>
        </div>

        {/* Expandable Market Data */}
        {showMarket && rates && (
          <div className="px-4 py-2 bg-[#070708] border-t border-zinc-800/30 animate-slideDown">
            <div className="grid grid-cols-4 gap-2 text-center">
              {rates.bist100 && (
                <div className="bg-zinc-900/50 rounded-lg p-2">
                  <p className="text-[9px] text-zinc-500 uppercase">BIST</p>
                  <p className="text-xs text-white font-semibold">{rates.bist100}</p>
                  {rates.bist100_change != null && (
                    <p className={`text-[10px] ${rates.bist100_change >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {rates.bist100_change >= 0 ? '+' : ''}{rates.bist100_change}%
                    </p>
                  )}
                </div>
              )}
              {rates.gold_try && (
                <div className="bg-zinc-900/50 rounded-lg p-2">
                  <p className="text-[9px] text-zinc-500 uppercase">ALTIN</p>
                  <p className="text-xs text-amber-400 font-semibold">{rates.gold_try}</p>
                  <p className="text-[10px] text-zinc-500">TL/gr</p>
                </div>
              )}
              <div className="bg-zinc-900/50 rounded-lg p-2">
                <p className="text-[9px] text-zinc-500 uppercase">USD</p>
                <p className="text-xs text-green-400 font-semibold">{rates.usd_try}</p>
                <p className="text-[10px] text-zinc-500">TL</p>
              </div>
              <div className="bg-zinc-900/50 rounded-lg p-2">
                <p className="text-[9px] text-zinc-500 uppercase">EUR</p>
                <p className="text-xs text-blue-400 font-semibold">{rates.eur_try}</p>
                <p className="text-[10px] text-zinc-500">TL</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Breaking News Ticker */}
      {breakingNews.length > 0 && (
        <div className="bg-[#0c0c0d] border-b border-zinc-800/30">
          <div className="flex items-center h-9 px-3 overflow-hidden">
            <div className="flex-shrink-0 flex items-center gap-1.5 mr-3">
              <Zap className="w-3.5 h-3.5 text-red-500 animate-pulse" />
              <span className="text-[10px] font-bold text-red-500 uppercase tracking-wider whitespace-nowrap">
                {t('breakingNews')}
              </span>
            </div>
            
            <div className="flex-1 overflow-hidden relative h-5">
              {breakingNews.map((item, i) => (
                <Link 
                  key={i}
                  to={`/haber/${item.slug || item.id}`}
                  className={`absolute inset-0 flex items-center text-[11px] text-zinc-400 transition-all duration-500 ${
                    i === currentNewsIndex 
                      ? 'opacity-100 translate-y-0' 
                      : 'opacity-0 translate-y-4'
                  }`}
                  data-testid={`mobile-ticker-${i}`}
                >
                  <span className="truncate">{item.title}</span>
                </Link>
              ))}
            </div>
            
            {/* Dots indicator */}
            <div className="flex-shrink-0 flex items-center gap-1 ml-2">
              {breakingNews.map((_, i) => (
                <span 
                  key={i}
                  className={`w-1 h-1 rounded-full transition-all duration-300 ${
                    i === currentNewsIndex ? 'bg-amber-500 w-2' : 'bg-zinc-700'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default MobileHeader;
